import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { IconRefresh } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';

// Lightweight cron -> English description for 6-field EasyCron (sec min hour dom mon dow)
function humanizePart(part: string, unit: string, names?: Record<number, string>) {
  if (part === '*') return `every ${unit}`;
  if (part.startsWith('*/')) {
    const n = parseInt(part.slice(2), 10);
    if (!Number.isNaN(n) && n > 0) return `every ${n} ${unit}${n > 1 ? 's' : ''}`;
  }
  if (/^\d+$/.test(part)) {
    const v = parseInt(part, 10);
    if (names && names[v]) return `on ${names[v]}`;
    return `at ${v} ${unit}${v !== 1 ? 's' : ''}`;
  }
  if (part.includes(',')) {
    const vals = part.split(',').map((s) => s.trim()).filter(Boolean);
    const mapped = names ? vals.map((x) => names[parseInt(x, 10)] || x) : vals;
    return `at ${mapped.join(', ')} ${unit}${vals.length > 1 ? 's' : ''}`;
  }
  if (part.includes('-')) {
    const [a, b] = part.split('-');
    const av = parseInt(a, 10);
    const bv = parseInt(b, 10);
    const aName = names && names[av] ? names[av] : a;
    const bName = names && names[bv] ? names[bv] : b;
    return `from ${aName} to ${bName} ${unit}${a !== b ? 's' : ''}`;
  }
  return `custom ${unit}`;
}

function parseList(part: string, names?: Record<number, string>) {
  const vals = part.split(',').map((s) => s.trim()).filter(Boolean);
  return vals.map((v) => {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && names && names[n]) return names[n];
    return v;
  });
}

function pad2(n: number | string) {
  const x = typeof n === 'number' ? n : parseInt(n, 10);
  return String(x).padStart(2, '0');
}

function describeCronExpression(expr: string, timeZone?: string) {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5 && parts.length !== 6) {
    return 'Invalid cron expression';
  }
  const [sec, min, hour, dom, mon, dow] =
    parts.length === 6 ? parts : ['0', parts[0], parts[1], parts[2], parts[3], parts[4]];

  const monthNames: Record<number, string> = {
    1: 'January', 2: 'February', 3: 'March', 4: 'April', 5: 'May', 6: 'June',
    7: 'July', 8: 'August', 9: 'September', 10: 'October', 11: 'November', 12: 'December',
  };
  const dayNames: Record<number, string> = {
    0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday',
  };

  const tzSuffix = timeZone ? ` (Timezone ${timeZone})` : '';

  if (min.startsWith('*/') && hour === '*' && dom === '*' && mon === '*' && dow === '*') {
    const n = parseInt(min.slice(2), 10);
    if (!Number.isNaN(n) && n > 0) {
      return `Every ${n} minute${n > 1 ? 's' : ''}${tzSuffix}`;
    }
  }

  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && mon === '*' && dow === '*') {
    return `At ${pad2(hour)}:${pad2(min)} every day${tzSuffix}`;
  }

  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && mon === '*' && dow !== '*') {
    const days = parseList(dow, dayNames);
    return `At ${pad2(hour)}:${pad2(min)} on ${days.join(', ')}${tzSuffix}`;
  }

  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dow === '*' && dom !== '*') {
    const days = parseList(dom);
    if (mon === '*') {
      return `At ${pad2(hour)}:${pad2(min)} on day ${days.join(', ')} of every month${tzSuffix}`;
    }
    const months = parseList(mon, monthNames);
    return `At ${pad2(hour)}:${pad2(min)} on day ${days.join(', ')} in ${months.join(', ')}${tzSuffix}`;
  }

  const secText = humanizePart(sec, 'second');
  const minText = humanizePart(min, 'minute');
  const hourText = humanizePart(hour, 'hour');
  const domText = humanizePart(dom, 'day-of-month');
  const monText = humanizePart(mon, 'month', monthNames);
  const dowText = humanizePart(dow, 'day-of-week', dayNames);
  return `${secText}; ${minText}; ${hourText}; ${domText}; ${monText}; ${dowText}${tzSuffix}`;
}

export function EasyCronPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'paused' | 'all'>('active');

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.get('/monitoring/easycron/jobs');
      const data = resp.data?.data;
      const list = Array.isArray(data?.cron_jobs)
        ? data.cron_jobs
        : (Array.isArray(resp.data?.cron_jobs) ? resp.data.cron_jobs : []);
      const computed = list.map((job: any) => {
        const cron = String(job?.cron_expression || '').trim();
        const tz = String(job?.timezone || '').trim();
        const scheduleText = cron ? describeCronExpression(cron, tz) : '—';
        return { ...job, _scheduleText: scheduleText };
      });
      setJobs(computed);
      setLastUpdatedAt(new Date());
    } catch (e: any) {
      setError('Failed to load EasyCron jobs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  const { activeCount, pausedCount, filteredJobs } = useMemo(() => {
    const active = jobs.filter((j) => Number(j?.status) === 1);
    const paused = jobs.filter((j) => Number(j?.status) === 0);
    const filtered =
      activeTab === 'active' ? active :
      activeTab === 'paused' ? paused :
      jobs;
    const getName = (j: any) => (j?.cron_job_name || String(j?.cron_job_id) || '').toString().toLowerCase();
    const isFailing = (j: any) => Number(j?.current_failures) > 0;
    const sorted = [...filtered].sort((a, b) => {
      const af = isFailing(a) ? 1 : 0;
      const bf = isFailing(b) ? 1 : 0;
      if (af !== bf) return bf - af;
      const an = getName(a);
      const bn = getName(b);
      return an.localeCompare(bn);
    });
    return {
      activeCount: active.length,
      pausedCount: paused.length,
      filteredJobs: sorted,
    };
  }, [jobs, activeTab]);

  if (isLoading && jobs.length === 0) {
    return <PageLoader message="Loading EasyCron jobs..." />;
  }

  return (
    <div>
      <PageHeader
        title="EasyCron Job Status"
        subtitle="Monitor EasyCron job health from within the toolbox"
        action={
          <Button
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <IconRefresh className="size-4" />
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <MainCard>
        <div className="flex items-center gap-3 mb-4">
          <Badge>
            {jobs.length} job{jobs.length === 1 ? '' : 's'}
          </Badge>
          {lastUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Last refreshed: {lastUpdatedAt.toLocaleString()}
            </span>
          )}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'active' | 'paused' | 'all')}
          className="mb-4"
        >
          <TabsList>
            <TabsTrigger value="active">Active ({activeCount})</TabsTrigger>
            <TabsTrigger value="paused">Paused ({pausedCount})</TabsTrigger>
            <TabsTrigger value="all">All ({jobs.length})</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-col gap-3">
          {filteredJobs.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No jobs found.
            </p>
          )}
          {filteredJobs.map((job: any) => {
            const status = job?.status;
            const name = job?.cron_job_name || String(job?.cron_job_id) || 'Unnamed';
            const cron = job?.cron_expression;
            const tz = job?.timezone;
            const url = job?.url;
            const totalSuccesses = job?.total_successes;
            const totalFailures = job?.total_failures;
            const currentFailures = Number(job?.current_failures) || 0;
            const isFailing = currentFailures > 0;
            const scheduleText = typeof job?._scheduleText === 'string' ? job._scheduleText : '—';
            
            return (
              <div
                key={job.cron_job_id || name}
                className={cn(
                  'p-4 border rounded-lg',
                  isFailing
                    ? 'border-red-500 bg-red-50 dark:bg-red-950/30'
                    : 'border-border bg-card'
                )}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-base font-semibold">{name}</h4>
                      {isFailing && (
                        <Badge variant="destructive" className="text-[0.65rem]">
                          Failing ({currentFailures})
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground break-all">
                      {url}
                    </span>
                  </div>
                  <Badge variant={Number(status) === 1 ? 'default' : 'secondary'}>
                    {Number(status) === 1 ? 'Active' : 'Paused'}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2">
                  <span className="text-xs text-muted-foreground">
                    Cron: {cron || '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Timezone: {tz || '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Schedule: {scheduleText}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Current failures: {currentFailures}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Total successes: {typeof totalSuccesses === 'number' ? totalSuccesses : '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    All-time failures: {typeof totalFailures === 'number' ? totalFailures : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </MainCard>
    </div>
  );
}
